import { forwardRef, type InputHTMLAttributes } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helpText?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const errorId = `${inputId}-error`
    const helpId = `${inputId}-help`
    const hasError = !!error

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label
            htmlFor={inputId}
            className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={hasError}
          aria-describedby={
            error ? errorId : helpText ? helpId : undefined
          }
          className={`w-full px-4 py-3 text-[14px] bg-swiss-white border transition-colors duration-150 focus:outline-none ${
            hasError
              ? 'border-swiss-red focus:border-swiss-red'
              : 'border-swiss-gray-200 focus:border-swiss-black'
          } ${className}`}
          {...props}
        />
        {error && (
          <span
            id={errorId}
            role="alert"
            className="text-[12px] text-swiss-red"
          >
            {error}
          </span>
        )}
        {helpText && !error && (
          <span id={helpId} className="text-[12px] text-swiss-gray-500">
            {helpText}
          </span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
